// Once we deprecate the older AudioRecorderVisualizer then this component can be renamed the same.

import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

/**
 * updateAudioRecorderBlob will be used to save recording blob in redux as draft
 */
import {
  updateAudioRecorderBlob,
  setAudioRecorderActive,
} from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { IconButton } from "@toddle-edu/ds-web";

import I18nHOC from "UIComponents/I18nHOC";
import FocusTrapWrapper from "UIComponents/FocusTrapWrapper";
import withRecorder from "../WithRecorder";

import classes from "./SpeechToTextVisualiser.scss";
import { CloseOutlined, TickLargeOutlined } from "@toddle-edu/ds-icons";
import { onEnterOrSpaceKeyDown } from "Utils";

// These keys are used as selector in ds-doc-editor for identifying the visualiser elements
// especially to bypass observer changes when applying visualiser related updates

const AUDIO_VISUALIZER_KEY = "audio-recorder-visualiser";
const AUDIO_VISUALIZER_CONFIRM_BUTTON_KEY =
  "audio-recorder-visualiser-complete-button";

const SpeechToTextVisualiser = forwardRef(
  (
    {
      // props from withRecorder
      cancelRecording,
      draftRecording,
      isPaused,
      isPermissionGranted,
      playerState,
      stopRecording,
      handleVisualiserRefUpdate,
      activeAudioRecorder,
      setAudioRecorderActive,
      timer,
      isInitializing,
      // -----------------------------
      recordButtonRef,
      _setToastMsg: setToastMsg,
      shouldShowCompleteButton,
      visualiserContainerStyle,
      visualiserInnerContainerStyle,
      shouldTrapFocus,
      scrollContainerRef,
      completeButtonSize,
      showTimer = false,
      getButtonPositionRect,
      getVisualiserContainerRect,
    },
    forwardedRef
  ) => {
    // refs
    const visualiserContainerRef = useRef(null);
    const completeButtonRef = useRef(null);
    const tickButtonRef = useRef(null);

    useImperativeHandle(forwardedRef, () => ({
      cancelRecording: () => {
        cancelRecording();
      },
    }));

    // This effect is used to position the visualiser and complete
    // recording button relative to the start recording button
    useLayoutEffect(() => {
      const updatePosition = () => {
        if (!recordButtonRef?.current || !completeButtonRef?.current) return;

        const buttonRect = recordButtonRef.current.getBoundingClientRect();
        const completeButtonRect = completeButtonRef.current;

        // align bottom right of the visualiser with the
        // top left of the record button
        if (visualiserContainerRef.current) {
          const container = visualiserContainerRef.current;

          container.style.top = `${
            buttonRect.top - container.offsetHeight - 4
          }px`;
          container.style.left = `${
            buttonRect.left - container.offsetWidth + buttonRect.width + 16
          }px`;
        }

        // overlap the record button, add our complete button on top of it
        if (completeButtonRef.current) {
          const container = completeButtonRef.current;

          container.style.top = `${buttonRect.top}px`;

          // Position the container to align its right edge with the record button's right edges
          container.style.left = `${
            buttonRect.left -
            container.offsetWidth +
            completeButtonRect.offsetWidth
          }px`;
        }

        if (getButtonPositionRect) {
          const container = completeButtonRef.current;
          const customRect = getButtonPositionRect({ buttonRect });
          if (customRect) {
            container.style.top = `${customRect.top}px`;
            container.style.left = `${customRect.left}px`;
          }
        }

        if (getVisualiserContainerRect) {
          const container = visualiserContainerRef.current;
          const customRect = getVisualiserContainerRect({
            buttonRect,
            visualiserContainer: container,
          });
          if (customRect) {
            container.style.top = `${customRect.top}px`;
            container.style.left = `${customRect.left}px`;
          }
        }
      };

      // Initial position
      updatePosition();

      // Pass scrollContainerRef if you want to update position on scroll
      if (scrollContainerRef) {
        scrollContainerRef.addEventListener("scroll", updatePosition);
      }

      window.addEventListener("resize", updatePosition);

      return () => {
        if (scrollContainerRef) {
          scrollContainerRef.removeEventListener("scroll", updatePosition);
        }
        window.removeEventListener("resize", updatePosition);
      };
    }, [playerState, scrollContainerRef, isInitializing]);

    const onVisualiserContainerRefUpdate = useCallback(node => {
      if (node !== null) {
        handleVisualiserRefUpdate(node);
      }
    }, []);

    const renderRecorder = () => {
      const canRenderRecorder = playerState !== "cancelled" && !draftRecording;

      if (
        isInitializing ||
        !canRenderRecorder ||
        !isPermissionGranted ||
        !_.includes(["record", "recording"], playerState)
      ) {
        // If the recorder is not in a state to render, return null
        return null;
      }

      const timerClasses = classNames(
        "text-body text-primary",
        classes.timerContainer
      );

      const handleCancelKeyDown = e => {
        if (e.key === "Escape") {
          cancelRecording();
          e.stopPropagation();
        }
      };

      return (
        <>
          {shouldShowCompleteButton ? (
            <div
              ref={completeButtonRef}
              className={classes.completeButton}
              data-key={AUDIO_VISUALIZER_CONFIRM_BUTTON_KEY}
            >
              <IconButton
                variant="progressive"
                ref={tickButtonRef}
                size={completeButtonSize}
                icon={<TickLargeOutlined />}
                onClick={stopRecording}
                aria-label="Save recording"
                onKeyDown={e => {
                  handleCancelKeyDown(e);
                  onEnterOrSpaceKeyDown(e, stopRecording);
                }}
                dsVersion={"2.0"}
              />
            </div>
          ) : null}
          <div
            className={classes.audioRecorderVisualizer}
            ref={visualiserContainerRef}
            style={visualiserContainerStyle}
            role="presentation"
            onKeyDown={handleCancelKeyDown}
            data-key={AUDIO_VISUALIZER_KEY}
          >
            <div
              className={classes.visualDetails}
              style={visualiserInnerContainerStyle}
            >
              <div
                className={classes.visualiserContainer}
                style={{ display: isPaused && "none" }}
                ref={onVisualiserContainerRefUpdate}
                aria-hidden
              />
              {showTimer && <div className={timerClasses}>{timer}</div>}
            </div>
            <div
              className={classes.closeButton}
              onClick={cancelRecording}
              role="button"
              tabIndex={0}
              onKeyDown={e => onEnterOrSpaceKeyDown(e, cancelRecording)}
              aria-label="Cancel recording"
            >
              <CloseOutlined variant="on" />
            </div>
          </div>
        </>
      );
    };

    useEffect(() => {
      setAudioRecorderActive({
        ...activeAudioRecorder,
        recorderAlreadyActive: true,
        closeActiveAudioRecorder: cancelRecording,
      });
      return () => {
        setAudioRecorderActive(null);
      };
    }, []);

    useEffect(() => {
      if (!isPermissionGranted) {
        setToastMsg({
          msg: "common:microphone_permission_message",
          type: "warning",
        });
        cancelRecording();
      }
    }, [isPermissionGranted]);

    // Focus the tick button when it's rendered
    useEffect(() => {
      if (
        shouldShowCompleteButton &&
        tickButtonRef.current &&
        playerState === "recording" &&
        !isInitializing
      ) {
        tickButtonRef.current.focus();
      }
    }, [shouldShowCompleteButton, playerState, isInitializing]);

    return (
      <FocusTrapWrapper isActive={shouldTrapFocus}>
        {renderRecorder()}
      </FocusTrapWrapper>
    );
  }
);

SpeechToTextVisualiser.displayName = "SpeechToTextVisualiser";

const mapActionCreators = {
  setToastMsg,
  // passing it as aliased prop because `withRecorder` HOC consumes
  // setToastMsg and doesn't pass it down to the component
  _setToastMsg: setToastMsg,
  updateAudioRecorderBlob,
  setAudioRecorderActive,
};

const mapStateToProps = (state, ownProps) => {
  const { visualiserColor: visualiserColorFromProps } = ownProps;

  const visualiserColor =
    visualiserColorFromProps || "var(--interactive-neutral)";

  return {
    activeAudioRecorder: _.get(
      state,
      "app_services.activeAudioRecorder",
      false
    ),
    visualiserColor,
    // this component does not support draft recordings
    saveDraftRecording: false,
  };
};

const propTypes = {
  autoStartRecording: PropTypes.bool,
  // record button ref is used for placement
  recordButtonRef: PropTypes.object.isRequired,
  visualiserColor: PropTypes.string,
  shouldShowCompleteButton: PropTypes.bool,
  visualiserContainerStyle: PropTypes.object,
  visualiserInnerContainerStyle: PropTypes.object,
  shouldTrapFocus: PropTypes.bool,
  scrollContainerRef: PropTypes.object,
  completeButtonSize: PropTypes.string,
  playBeepOnStart: PropTypes.bool,
};

const defaultProps = {
  /**
   * ! If setting autoStart value as false then it should be noted that the pause button is rendered rather than start recording
   * ! getting error with the isRecording props
   */
  autoStartRecording: true,
  recordButtonRef: null,
  shouldShowCompleteButton: true,
  visualiserContainerStyle: {},
  visualiserInnerContainerStyle: {},
  shouldTrapFocus: true, // whether to trap focus within the visualiser
  scrollContainerRef: null,
  completeButtonSize: "small",
  playBeepOnStart: true,
};

SpeechToTextVisualiser.propTypes = propTypes;
SpeechToTextVisualiser.defaultProps = defaultProps;

const ComposedSpeechToTextVisualiser = compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, null, { forwardRef: true }),
  withRecorder
)(SpeechToTextVisualiser);

ComposedSpeechToTextVisualiser.propTypes = propTypes;
ComposedSpeechToTextVisualiser.defaultProps = defaultProps;

export default ComposedSpeechToTextVisualiser;
